import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./PlacesPage.css";
import { getFilteredPlacesByCity, getTopDestinations } from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import Zoom from "@mui/material/Zoom";
import Fade from "@mui/material/Fade";

const LIMIT = 4;

const PlacesPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const { cityId } = useParams();
  const [placesData, setPlacesData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState(false);
  const [fade, setFade] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await getTopDestinations();
        setCategories(cats);
      } catch {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  const handlePlaceClick = (PlaceID) => {
    navigate(`/place-details/${PlaceID}`);
  };

  // Fetch places when filters/search/page change
  useEffect(() => {
    if (!cityId) {
      navigate("/");
      return;
    }

    const fetchPlacesData = async () => {
      setPlacesData(null);
      try {
        const data = await getFilteredPlacesByCity(cityId, {
          categoryID: selectedCategory,
          search: searchTerm,
          page,
          limit: LIMIT,
        });

        if (data && data.cityDetails && data.places) {
          setPlacesData(data);
          setChecked(true);
          setFade(true);
          setError(null);
        } else {
          throw new Error("Invalid data structure");
        }
      } catch (error) {
        setError(t("Failed to fetch places data", "Failed to fetch places data"));
      }
    };

    fetchPlacesData();
  }, [cityId, selectedCategory, searchTerm, page, navigate, t]);

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!placesData) {
    return (
      <div className="spinner-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const { cityDetails, places, pagination } = placesData;
  const city = cityDetails;
  const totalPages = Math.ceil((pagination?.count || 0) / LIMIT);

  return (
    <div className={`pplaces-page ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
      {/* HERO */}
      <Fade in={fade} timeout={1500}>
        <div className="city-hero">
          <img
            src={city.CityImage}
            alt={city.CityName}
            className="city-hero-image"
          />
          <div className="city-hero-overlay" />
          <div className="city-hero-content">
            <h1 className="city-hero-title">
              {city.CityName} / {city.CountryName}
            </h1>
            <p className="city-hero-subtitle">
              {t("Discover the best places, stories, and hidden gems of", "Discover the best places, stories, and hidden gems of")} {city.CityName}.
            </p>
            <div className="city-hero-meta">
              <span className="city-meta-chip">{t("Popular destination", "Popular destination")}</span>
              <span className="city-meta-chip">
                {pagination?.count || 0} {t("places to explore", "places to explore")}
              </span>
            </div>
          </div>
        </div>
      </Fade>

      {/* FILTERS */}
      <div className="places-filters">
        <input
          type="text"
          placeholder={t("Search places...", "Search places...")}
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
          className="places-search-input"
        />
        <div className="places-category-list">
          <button
            className={`places-category-btn${selectedCategory === "" ? " active" : ""}`}
            onClick={() => { setSelectedCategory(""); setPage(1); }}
          >
            {t("All", "All")}
          </button>
          {categories.map(cat => (
            <button
              key={cat.CategoryID}
              className={`places-category-btn${selectedCategory === cat.CategoryID ? " active" : ""}`}
              onClick={() => { setSelectedCategory(cat.CategoryID); setPage(1); }}
            >
              {t(cat.NameCategory, cat.NameCategory)}
            </button>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="city-main">
        {/* LEFT: info cards */}
        <div>
          <div className="city-info-card">
            <h2 className="city-info-title">{t("Discover", "Discover")} {city.CityName}</h2>
            <p className="city-info-text">{city.CityDescription}</p>
          </div>
          <div className="city-info-card">
            <h2 className="city-info-title">{t("A Journey Through History", "A Journey Through History")}</h2>
            <p className="city-info-text">{city.CityHistory}</p>
          </div>
        </div>

        {/* RIGHT: places list */}
        <div>
          <div className="places-section-header">
            <h2 className="places-section-title">
              {t("Top Places to Visit in", "Top Places to Visit in")} {city.CityName}
            </h2>
            <span className="places-section-sub">
              {pagination?.count || 0} {t("recommendations", "recommendations")}
            </span>
          </div>

          <div className="pplaces-list">
            {places.map((place, index) => (
              <Zoom
                in={checked}
                key={place.PlaceID}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="pplace-card"
                  onClick={() => handlePlaceClick(place.PlaceID)}
                >
                  <div className="pplace-number">#{(page - 1) * LIMIT + index + 1}</div>
                  <img
                    src={place.placeImage}
                    alt={place.PlaceName}
                    className="pplace-image"
                  />
                  <div className="pplace-details">
                    <h3>{place.PlaceName}</h3>
                    <p className="pplace-description">
                      {place.PlaceDescription}
                    </p>
                    <div className="pplace-footer">
                      <span className="pplace-category">{place.Category}</span>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${place.Location.x},${place.Location.y}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="place-location-link"
                      >
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className="place-location-icon"
                        />
                        <span>{t("View on map", "View on map")}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </Zoom>
            ))}
          </div>

          {/* Pagination */}
          <div className="places-pagination">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`places-page-btn${page === i + 1 ? " active" : ""}`}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        className="back-to-cities-btn"
        onClick={() => navigate(`/cities/${city.CountryID}`)}
        style={{ marginBottom: "18px" }}
      >
        ← {t("Back to Cities", "Back to Cities")}
      </button>
    </div>
  );
};

export default PlacesPage;